import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import Image from "../components/elements/Image";
import Logo from "../assets/images/logo-memory-doodle.svg";
import { classNames } from "../helpers/classNames";
import { useSelector, useDispatch } from "react-redux";
import { resetStep, setIsComingFromNonUserRoute } from "../redux/slice/stepSlice";
import { setCoverImages, setImages } from "../redux/slice/sketchSlice";
import { pruneSelectedPlanLocalStorage } from "../services/shopifyCartService";
import { selectBooksLoadedAt } from "../redux/slice/profileSlice";
import LogoutModal from "../components/LogoutModal/LogoutModal";
import { isEtsyUser, isEtsySession } from "../services/etsyService";

const DefaultHeader = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [, setScrolled] = useState(false);
  const [authenticationModal, setAuthenticationModal] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [, setCartCount] = useState(0);
  const [, setCartItems] = useState([]);
  const [showCartDropdown, setShowCartDropdown] = useState(false);
  const [showNoPlanModal, setShowNoPlanModal] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [, setActiveSection] = useState("banner");
  const { session } = useSelector((state) => state?.authProvider);
  const Profile = useSelector((state) => state?.profile?.data);
  const { myBooks } = useSelector((state) => state?.profile);
  const booksLoadedAt = useSelector(selectBooksLoadedAt);
  const { activeBookId } = useSelector((state) => state?.stepFlow);

  const hasPlacedOrder = Profile?.bookData?.some(book => book.isOrderPlaces === true);

  const menu = useMemo(
    () => [
      { _id: 1, name: "Home", link: "/" },
      { _id: 2, name: "How It Works", link: "/how-it-works" },
    ],
    []
  );

  const getItemQuantity = (item) => {
    if (!item) return 0;
    const pdQty = Number(item?.planPricingData?.quantity);
    if (!Number.isNaN(pdQty) && pdQty > 0) return pdQty;
    const rootQty = Number(item?.quantity);
    if (!Number.isNaN(rootQty) && rootQty > 0) return rootQty;
    return 0;
  };

  const calculateCartCount = (items) => {
    if (!Array.isArray(items) || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      const qty = getItemQuantity(item);
      return sum + (qty > 0 ? qty : 1);
    }, 0);
  };

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      setScrolled(isScrolled);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
      let currentSection = null;
      for (const item of menu) {
        const section = document.getElementById(item.link);
        if (section) {
          const offsetTop = section.offsetTop - 96;
          const offsetBottom = offsetTop + section.offsetHeight;
          if (window.scrollY >= offsetTop && window.scrollY < offsetBottom) {
            currentSection = item.link;
            break;
          }
        }
      }
      setActiveSection(currentSection);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [menu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCartDropdown && !event.target.closest(".cart-dropdown-container")) {
        setShowCartDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCartDropdown]);

  useEffect(() => {
    const getCartData = () => {
      try {
        // No-op unless this document's book list is authoritative for the signed-in customer.
        pruneSelectedPlanLocalStorage(myBooks || [], booksLoadedAt);

        const selectedPlan = localStorage.getItem("selectedPlan");
        const unassignedPlans = localStorage.getItem("unassignedPlans");

        if (selectedPlan) {
          let parsedPlan = JSON.parse(selectedPlan);

          if (
            typeof parsedPlan === "object" &&
            parsedPlan !== null &&
            !Array.isArray(parsedPlan)
          ) {
            parsedPlan = [parsedPlan];
            localStorage.setItem("selectedPlan", JSON.stringify(parsedPlan));
          }

          if (Array.isArray(parsedPlan) && parsedPlan.length > 0) {
            setCartItems(parsedPlan);
            setCartCount(calculateCartCount(parsedPlan));
          } else {
            setCartItems([]);
            setCartCount(0);
          }
        }
        else if (unassignedPlans) {
          try {
            let parsedUnassigned = JSON.parse(unassignedPlans);

            if (
              typeof parsedUnassigned === "object" &&
              parsedUnassigned !== null &&
              !Array.isArray(parsedUnassigned)
            ) {
              parsedUnassigned = [parsedUnassigned];
            }

            if (Array.isArray(parsedUnassigned) && parsedUnassigned.length > 0) {
              setCartCount(calculateCartCount(parsedUnassigned));
              setCartItems([]);
            } else {
              setCartItems([]);
              setCartCount(0);
            }
          } catch (err) {
            console.error("Error parsing unassignedPlans:", err);
            setCartItems([]);
            setCartCount(0);
          }
        }
        else {
          setCartItems([]);
          setCartCount(0);
        }
      } catch (error) {
        console.error("Error parsing selectedPlan from localStorage:", error);
        setCartItems([]);
        setCartCount(0);
      }
    };

    getCartData();
    window.addEventListener("storage", getCartData);
    window.addEventListener("cartUpdated", getCartData);

    return () => {
      window.removeEventListener("storage", getCartData);
      window.removeEventListener("cartUpdated", getCartData);
    };
  }, [booksLoadedAt, myBooks]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest(".mobile-menu") && !event.target.closest(".hamburger-button")) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const onLogoRedirection = () => {
    window.location.href = process.env.REACT_APP_SHOPIFY_URL;
  };

  const toggleCartDropdown = () => {
    try {
      const preSelectedPlan = localStorage.getItem("selectedPlan");

      if (!preSelectedPlan) {
        setShowNoPlanModal(true);
        return;
      }

      let parsed;
      try {
        parsed = JSON.parse(preSelectedPlan);
      } catch (err) {
        console.error(" Failed to parse selectedPlan from localStorage:", err);
        setShowNoPlanModal(true);
        return;
      }

      if (Array.isArray(parsed)) {
        if (parsed.length === 0) {
          setShowNoPlanModal(true);
          return;
        }
      } else {
        setShowNoPlanModal(true);
        return;
      }

      navigate("/finalize-order");

    } catch (error) {
      console.error(" Cart redirect failed from header:", error);
      setShowNoPlanModal(true);
    }
  };

  const orderedBooks = myBooks?.filter((book) => !!book.isOrderPlaces) || [];
  // New customers with one book are kept in that book's flow; once there are several (or an
  // order), My Books is the only way back to the ones not in the cart.
  const showMyBooks = orderedBooks.length > 0 || myBooks?.length > 1;

  const onColoringBookNavigate = () => {
    if (!!activeBookId && showMyBooks) {
      dispatch(resetStep());
      dispatch(setCoverImages([]));
      dispatch(setImages([]));

      if (location.pathname === "/") dispatch(setIsComingFromNonUserRoute(true));
    } else if (
      !activeBookId &&
      location.pathname.includes("create-book") &&
      myBooks?.length <= 1
    ) {
      return;
    }

    navigate("/dashboard");
    setMenuOpen(false);
  };

  const isAuthenticated = !!Profile?._id;

  // An Etsy buyer gets no site chrome. Every link in this header belongs to
  // Shopify: My Books is a library with add-to-cart buttons, Finalize Order is
  // the checkout they already paid on Etsy, Sign Out logs out of a Shopify
  // account they do not have, and the logo leaves for the Shopify storefront.
  // They arrived from one Etsy order to build one book, so the wizard gets the
  // same centred, unclickable logo that EtsyLayout gives /etsy and /etsy/done.
  //
  // Placed after every hook above, so the hook order never changes with it.
  if (isEtsyUser(Profile) || isEtsySession()) {
    return (
      <header className="sticky z-50 top-0 w-full flex items-center justify-center h-24 bg-white border-b border-md-pink-light-1">
        <div className="w-full max-w-40 sm:max-w-60 h-12 sm:h-14">
          <Image
            src={Logo}
            alt={"MemoryDoodle"}
            width={"100%"}
            height={"100%"}
            effect={"blur"}
            classes={"object-contain object-center"}
          />
        </div>
      </header>
    );
  }

  return (
    <>
      <header
        className={classNames(
          "sticky z-50 top-0 w-full flex items-center transition-all duration-300 h-24 bg-white border-b border-md-pink-light-1"
        )}
      >
        <div className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xls:max-w-6xl xl:max-w-7xl mx-auto px-4 lg:px-20">
          <div className="flex items-center gap-0 xl:gap-2 sm:gap-16 3xl:gap-40 flex-shrink-0 relative">
            <div
              className={classNames(
                "w-full max-w-40 sm:max-w-60 transition-all duration-300 overflow-hidden h-12 sm:h-14 mx-auto sm:ml-0 absolute sm:relative left-0 right-0"
              )}
            >
              <div
                className="relative flex w-full h-full overflow-hidden cursor-pointer"
                onClick={onLogoRedirection}
              >
                <Image
                  src={Logo}
                  alt={"Logo"}
                  width={"100%"}
                  height={"100%"}
                  effect={"blur"}
                  classes={"object-contain object-left"}
                />
              </div>
            </div>

            {/* Desktop & Mobile Navigation */}
            <div className="ml-auto flex items-center gap-2 md:gap-4">
              {/* Desktop Menu - Non-authenticated */}
              {!isAuthenticated && session?.status !== "loading" && (
                <div className="hidden xl:flex items-center gap-2 sm:gap-10">
                  {menu?.map((item) => (
                    <div className="relative" key={item._id}>
                      <NavLink
                        to={item.link}
                        className={({ isActive }) =>
                          classNames(
                            "px-3 inline-block text-sm sm:text-base 2xl:text-lg font-semibold transition-all duration-200 hover:text-blue",
                            isActive ? "text-md-pink" : "text-md-dark"
                          )
                        }
                      >
                        {item.name}
                      </NavLink>
                    </div>
                  ))}
                </div>
              )}

              {/* Desktop Menu - Authenticated */}
              {isAuthenticated && session?.status !== "loading" && (
                <div className="hidden xl:flex items-center gap-2 sm:gap-10">
                  {showMyBooks && (
                    <NavLink
                      to="/dashboard"
                      onClick={onColoringBookNavigate}
                      className="inline-block font-heading text-sm sm:text-base 2xl:text-lg transition-all duration-200 hover:text-blue whitespace-nowrap text-md-dark font-semibold"
                    >
                      My Books
                    </NavLink>
                  )}
                  <button
                    onClick={toggleCartDropdown}
                    className="inline-block font-heading text-sm sm:text-base 2xl:text-lg transition-all duration-200 hover:text-blue whitespace-nowrap text-md-dark font-semibold"
                  >
                    Finalize Order
                  </button>
                  <button
                    type="button"
                    onClick={() => setLogoutModalOpen(true)}
                    className="inline-block font-heading text-sm sm:text-base 2xl:text-lg transition-all duration-200 hover:text-blue whitespace-nowrap text-md-dark font-semibold"
                  >
                    Sign Out
                  </button>
                </div>
              )}

              {/* Hamburger Menu Button - Only for authenticated users */}
              {isAuthenticated && session?.status !== "loading" && (
                <button
                  onClick={() => setMenuOpen(!isMenuOpen)}
                  className="xl:hidden hamburger-button flex items-center justify-center px-1 text-md-dark hover:text-md-pink transition-colors"
                  aria-label="Toggle menu"
                >
                  {isMenuOpen ? (
                    <i className="fa-solid fa-xmark text-2xl"></i>
                  ) : (
                    <i className="fa-solid fa-bars text-2xl"></i>
                  )}
                </button>
              )}

              {/* Hamburger Menu Button - For non-authenticated users */}
              {!isAuthenticated && session?.status !== "loading" && (
                <button
                  onClick={() => setMenuOpen(!isMenuOpen)}
                  className="xl:hidden hamburger-button flex items-center justify-center w-10 h-10 text-md-dark hover:text-md-pink transition-colors"
                  aria-label="Toggle menu"
                >
                  {isMenuOpen ? (
                    <i className="fa-solid fa-xmark text-2xl"></i>
                  ) : (
                    <i className="fa-solid fa-bars text-2xl"></i>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <div
          className={classNames(
            "mobile-menu fixed top-24 right-0 w-full bg-white shadow-2xl transition-transform duration-300 ease-in-out xl:hidden z-50 border-l border-gray-200",
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          )}
          style={{ maxHeight: 'calc(100vh - 6rem)', overflowY: 'auto' }}
        >
          <div className="py-4 px-6">
            {/* Authenticated User Menu */}
            {isAuthenticated && session?.status !== "loading" && (
              <div className="flex flex-col gap-4">
                {showMyBooks && (
                  <NavLink
                    to="/dashboard"
                    onClick={onColoringBookNavigate}
                    className={({ isActive }) =>
                      classNames(
                        "py-3 px-4 rounded-lg font-heading text-base font-semibold transition-all duration-200 hover:bg-md-pink/10",
                        isActive ? "text-md-pink bg-md-pink/5" : "text-md-dark"
                      )
                    }
                  >
                    <i className="fa-solid fa-book mr-3"></i>
                    My Books
                  </NavLink>
                )}
                <button
                  onClick={() => {
                    toggleCartDropdown();
                    setMenuOpen(false);
                  }}
                  className="py-3 px-4 rounded-lg font-heading text-base font-semibold transition-all duration-200 hover:bg-md-pink/10 text-md-dark text-left"
                >
                  <i className="fa-solid fa-cart-shopping mr-3"></i>
                  Finalize Order
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setLogoutModalOpen(true);
                  }}
                  className="py-3 px-4 rounded-lg font-heading text-base font-semibold transition-all duration-200 hover:bg-md-pink/10 text-md-dark text-left"
                >
                  <i className="fa-solid fa-right-from-bracket mr-3"></i>
                  Sign Out
                </button>
              </div>
            )}

            {/* Non-Authenticated User Menu */}
            {!isAuthenticated && session?.status !== "loading" && (
              <div className="flex flex-col gap-4">
                {menu?.map((item) => (
                  <NavLink
                    key={item._id}
                    to={item.link}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      classNames(
                        "py-3 px-4 rounded-lg text-base font-semibold transition-all duration-200 hover:bg-md-pink/10",
                        isActive ? "text-md-pink bg-md-pink/5" : "text-md-dark"
                      )
                    }
                  >
                    {item.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div
          className={classNames(
            "fixed inset-0 bg-black/50 transition-opacity duration-300 xl:hidden",
            isMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
          )}
          style={{ top: '6rem' }}
          onClick={() => setMenuOpen(false)}
        />
      </header>

      {/* No Plan Modal */}
      <LogoutModal isOpen={logoutModalOpen} setIsOpen={setLogoutModalOpen} />

      {showNoPlanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Ready to Order?</h3>
              <button
                onClick={() => setShowNoPlanModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <i className="fa-solid fa-xmark text-xl"></i>
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              It looks like your order is empty. Choose a book from your collection or create a new book before finalizing your order.
            </p>
            <button
              onClick={() => setShowNoPlanModal(false)}
              className="w-full bg-md-pink hover:bg-md-pink/90 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

    </>
  );
};

export default DefaultHeader;